import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../api/admin';
import { format } from 'date-fns';
import './TablePage.css';
import { SEMANTIC } from '../theme/chart';

const STATUS_LABELS: Record<string, string> = {
  pending: '대기중',
  delivered: '전달됨',
  viewed: '확인함',
  guessed: '픽 완료',
  awaiting_result: '결과대기',
  matched: '매칭성공',
  failed: '매칭실패',
  expired: '만료',
};

const PHASE_LABELS: Record<string, string> = {
  none: '없음',
  hint: '힌트 (D-6)',
  message_1: '1단 (D-4)',
  message_2: '2단 (D-2)',
  message_3: '3단 (D-1)',
  full: '전체공개 (D-day)',
};

const PHASE_ORDER = ['none', 'hint', 'message_1', 'message_2', 'message_3', 'full'];

function getNextLabel(pick: any): string | null {
  if (['matched', 'failed', 'expired'].includes(pick.status)) return null;
  const idx = PHASE_ORDER.indexOf(pick.revealPhase);
  if (idx < PHASE_ORDER.length - 1) return PHASE_LABELS[PHASE_ORDER[idx + 1]];
  if (pick.status !== 'awaiting_result') return '결과대기';
  return '결과발표 (1분 뒤)';
}

export default function PickDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [pick, setPick] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const navigate = useNavigate();

  const loadPick = useCallback(() => {
    if (id) {
      setLoading(true);
      adminApi.getPickDetail(id).then(setPick).finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => { loadPick(); }, [loadPick]);

  const showMsg = (text: string, type: 'success' | 'error') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleNextPhase = async () => {
    if (!id) return;
    try {
      const result = await adminApi.simulateProgress(id, 'next_phase');
      showMsg(`→ ${STATUS_LABELS[result.status] || result.status} / ${PHASE_LABELS[result.revealPhase] || result.revealPhase}`, 'success');
      loadPick();
    } catch (e: any) {
      showMsg(e.response?.data?.message || '실패', 'error');
    }
  };

  const handleSimulate = async (target: string, label: string) => {
    if (!id) return;
    try {
      const result = await adminApi.simulateProgress(id, target);
      showMsg(`${label} → ${STATUS_LABELS[result.status]} / ${PHASE_LABELS[result.revealPhase] || result.revealPhase}`, 'success');
      loadPick();
    } catch (e: any) {
      showMsg(e.response?.data?.message || '실패', 'error');
    }
  };

  if (loading) return <div className="loading">로딩 중...</div>;
  if (!pick) return <div className="loading">픽을 찾을 수 없습니다</div>;

  const phaseIdx = PHASE_ORDER.indexOf(pick.revealPhase);
  const progressPct = ((phaseIdx) / (PHASE_ORDER.length - 1)) * 100;
  const isTerminal = ['matched', 'failed', 'expired'].includes(pick.status);
  const nextLabel = getNextLabel(pick);

  const fields = [
    { label: '보낸사람', value: pick.picker?.phone || pick.pickerId },
    { label: '받는사람', value: `${pick.pickedContactName || '-'} (${pick.pickedPhone || '-'})` },
    { label: '받는사람 (유저)', value: pick.picked?.phone || '-' },
    { label: '채널', value: pick.pickChannel || 'contact' },
    { label: '힌트', value: `${pick.hintText || '-'} (${pick.hintId || '-'})` },
    { label: '메시지', value: [pick.messageToneId, pick.messageCoreId, pick.messageEndingId].filter(Boolean).join(' / ') || '-' },
    { label: '보호 픽', value: pick.isProtectedPick ? 'O' : 'X' },
    // ⛔ 응답 «대상 번호» 는 서버가 주지 않는다 — 해시만 저장한다 (정책 §8)
    { label: '픽기회', value: pick.guessOpen ? (pick.guessedAt ? '사용함' : '오픈') : 'X' },
    { label: '체인 원본', value: pick.chainFromPickId || '-' },
    { label: '생성일', value: format(new Date(pick.createdAt), 'yyyy-MM-dd HH:mm') },
    { label: '전달일', value: pick.deliveredAt ? format(new Date(pick.deliveredAt), 'yyyy-MM-dd HH:mm') : '-' },
    { label: '결과 발표일', value: pick.resultRevealAt ? format(new Date(pick.resultRevealAt), 'yyyy-MM-dd HH:mm') : '-' },
    { label: '긁기', value: pick.scratchedPhases?.join(', ') || '-' },
  ];

  return (
    <div className="table-page">
      <button className="back-btn" onClick={() => navigate('/picks')}>← 픽 목록</button>
      <h2>픽 상세</h2>

      {message && <div className={`settings-message ${messageType}`}>{message}</div>}

      {/* 상태 & 진행도 */}
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20, boxShadow: 'var(--shadow-card)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>상태</span>
            <div style={{ fontSize: 20, fontWeight: 700, color: isTerminal ? (pick.status === 'matched' ? SEMANTIC.matched : SEMANTIC.failed) : 'var(--ink-body)' }}>
              {STATUS_LABELS[pick.status] || pick.status}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>공개단계</span>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--info-ink)' }}>
              {PHASE_LABELS[pick.revealPhase] || pick.revealPhase}
            </div>
          </div>
        </div>

        {/* 진행 바 */}
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progressPct)}
          aria-label="공개 진행도"
          style={{ position: 'relative', height: 8, background: 'var(--line)', borderRadius: 4, marginBottom: 8, overflow: 'hidden' }}
        >
          {/* width 가 아니라 transform 을 움직인다 — width 애니메이션은 매 프레임 레이아웃을 다시 계산한다.
              트랙에 overflow:hidden 을 줘서 채움 막대는 radius 없이 잘라 쓴다(scaleX 가 radius 를 찌그러뜨린다). */}
          <div
            style={{
              height: '100%',
              width: '100%',
              transformOrigin: 'left center',
              transform: `scaleX(${Math.max(0, Math.min(100, progressPct)) / 100})`,
              background: isTerminal ? 'var(--neutral-ink)' : 'var(--info-ink)',
              transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-soft)' }}>
          {PHASE_ORDER.map((p, i) => (
            <span key={p} style={{ fontWeight: i <= phaseIdx ? 700 : 400, color: i <= phaseIdx ? 'var(--info-ink)' : 'var(--ink-soft)' }}>
              {['없음', '힌트', '1단', '2단', '3단', '전체'][i]}
            </span>
          ))}
        </div>

        {/* 다음 단계로 버튼 */}
        {!isTerminal && nextLabel && (
          <button
            onClick={handleNextPhase}
            style={{
              marginTop: 16, width: '100%', padding: '14px 0',
              background: 'var(--info-ink)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 700, fontSize: 15,
            }}
          >
            다음 단계로 ▶ {nextLabel}
          </button>
        )}

        {/* 빠른 스킵 */}
        {!isTerminal && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => handleSimulate('full_reveal', '전체공개')}
              style={{ flex: 1, padding: '8px 0', background: 'var(--neutral-tint)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 12, color: 'var(--ink-soft)' }}
            >
              전체공개 스킵
            </button>
            <button
              onClick={() => handleSimulate('result_ready', '결과발표')}
              style={{ flex: 1, padding: '8px 0', background: 'var(--neutral-tint)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 12, color: 'var(--ink-soft)' }}
            >
              결과발표 스킵
            </button>
          </div>
        )}
      </div>

      {/* 기본 정보 */}
      <div className="detail-grid">
        {fields.map((f) => (
          <div key={f.label} className="detail-field">
            <div className="detail-label">{f.label}</div>
            <div className="detail-value">{f.value}</div>
          </div>
        ))}
      </div>

      {/* 힌트 피드 */}
      {pick.hintFeedItems?.length > 0 && (
        <>
          <h3>힌트 피드 ({pick.hintFeedItems.length})</h3>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>타입</th><th>내용</th><th>시간</th></tr></thead>
              <tbody>
                {pick.hintFeedItems.map((item: any) => (
                  <tr key={item.id}>
                    <td>{item.type}</td>
                    <td>{item.text}</td>
                    <td>{format(new Date(item.timestamp), 'MM-dd HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 만남 제안 */}
      {pick.meetingProposal && (
        <>
          <h3>만남 제안</h3>
          <div className="detail-grid">
            <div className="detail-field">
              <div className="detail-label">장소</div>
              <div className="detail-value">{pick.meetingProposal.placeName}</div>
            </div>
            <div className="detail-field">
              <div className="detail-label">날짜</div>
              <div className="detail-value">{pick.meetingProposal.date ? format(new Date(pick.meetingProposal.date), 'yyyy-MM-dd HH:mm') : '-'}</div>
            </div>
            <div className="detail-field">
              <div className="detail-label">확정</div>
              <div className="detail-value">{pick.meetingProposal.confirmed ? 'O' : 'X'}</div>
            </div>
          </div>
        </>
      )}

      {/* 고급 도구 (접기) */}
      <div style={{ marginTop: 24 }}>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{ background: 'none', border: 'none', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, padding: 0 }}
        >
          {showAdvanced ? '▼ 고급 도구 닫기' : '▶ 고급 도구'}
        </button>
        {showAdvanced && <AdvancedTools id={id!} pick={pick} onUpdate={loadPick} showMsg={showMsg} />}
      </div>
    </div>
  );
}

function AdvancedTools({ id, pick, onUpdate, showMsg }: { id: string; pick: any; onUpdate: () => void; showMsg: (t: string, ty: 'success' | 'error') => void }) {
  const STATUSES = [
    { value: 'pending', label: '대기중' }, { value: 'delivered', label: '전달됨' },
    { value: 'viewed', label: '확인함' }, { value: 'guessed', label: '픽 완료' },
    { value: 'awaiting_result', label: '결과대기' }, { value: 'matched', label: '매칭성공' },
    { value: 'failed', label: '매칭실패' }, { value: 'expired', label: '만료' },
  ];
  const PHASES = [
    { value: 'none', label: 'NONE' }, { value: 'hint', label: 'HINT' },
    { value: 'message_1', label: 'MSG1' }, { value: 'message_2', label: 'MSG2' },
    { value: 'message_3', label: 'MSG3' }, { value: 'full', label: 'FULL' },
  ];

  const [forceStatus, setForceStatus] = useState('');
  const [forcePhase, setForcePhase] = useState('');

  const handleForceStatus = async () => {
    if (!forceStatus) return;
    try {
      await adminApi.forcePickStatus(id, forceStatus);
      showMsg(`상태 → ${forceStatus}`, 'success');
      setForceStatus('');
      onUpdate();
    } catch (e: any) { showMsg(e.response?.data?.message || '실패', 'error'); }
  };

  const handleForcePhase = async () => {
    if (!forcePhase) return;
    try {
      await adminApi.forcePickPhase(id, forcePhase);
      showMsg(`공개단계 → ${forcePhase}`, 'success');
      setForcePhase('');
      onUpdate();
    } catch (e: any) { showMsg(e.response?.data?.message || '실패', 'error'); }
  };

  return (
    <div style={{ marginTop: 12, padding: 16, background: 'var(--surface-header)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
      <div className="settings-form">
        <div className="settings-row">
          <label>상태</label>
          <select value={forceStatus} onChange={(e) => setForceStatus(e.target.value)}>
            <option value="">선택</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={handleForceStatus} disabled={!forceStatus}>변경</button>
        </div>
        <div className="settings-row" style={{ marginTop: 8 }}>
          <label>공개단계</label>
          <select value={forcePhase} onChange={(e) => setForcePhase(e.target.value)}>
            <option value="">선택</option>
            {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button onClick={handleForcePhase} disabled={!forcePhase}>변경</button>
        </div>
      </div>
    </div>
  );
}
